import { useEffect, useState } from "react";
import type { Session } from "@supabase/supabase-js";

import { AdminUsers } from "~/components/admin-users";
import { Disaster } from "~/components/disaster";
import { LoginForm } from "~/components/login-form";
import { Inventory } from "~/components/inventory";
import { OperationLogs } from "~/components/operation-logs";
import { PaymentLookupPanel } from "~/components/payment-lookup";
import { Shipping } from "~/components/shipping";
import { StatusBoard } from "~/components/status-board";
import { SubscriptionCycles } from "~/components/subscription-cycles";
import { Alert, AlertDescription, AlertTitle } from "~/components/ui/alert";
import { Button } from "~/components/ui/button";
import { supabase, supabaseConfigurationError } from "~/lib/supabase";

export default function App() {
  const [session, setSession] = useState<Session | null | undefined>(undefined);
  const [authError, setAuthError] = useState<string | null>(null);
  const [isSigningOut, setIsSigningOut] = useState(false);
  const [activeTab, setActiveTab] = useState<
    | "status"
    | "cycles"
    | "inventory"
    | "shipping"
    | "disaster"
    | "logs"
    | "lookup"
    | "admins"
  >("status");

  useEffect(() => {
    const client = supabase;
    if (!client) {
      setSession(null);
      return;
    }

    let active = true;

    void client.auth.getSession().then(({ data, error }) => {
      if (!active) return;

      if (error) {
        setAuthError("保存されたセッションを確認できませんでした。");
        setSession(null);
        return;
      }
      setSession(data.session);
    });

    const {
      data: { subscription },
    } = client.auth.onAuthStateChange((_event, nextSession) => {
      if (!active) return;
      setAuthError(null);
      setSession(nextSession);
    });

    return () => {
      active = false;
      subscription.unsubscribe();
    };
  }, []);

  async function handleSignOut() {
    const client = supabase;
    if (!client) return;

    setIsSigningOut(true);
    setAuthError(null);
    const { error } = await client.auth.signOut();
    if (error) {
      setAuthError("ログアウトできませんでした。もう一度お試しください。");
    }
    setIsSigningOut(false);
  }

  if (supabaseConfigurationError || !supabase) {
    return (
      <main className="mx-auto flex min-h-screen max-w-2xl items-center px-4">
        <Alert variant="destructive">
          <AlertTitle>Supabase Auth の設定がありません</AlertTitle>
          <AlertDescription>{supabaseConfigurationError}</AlertDescription>
        </Alert>
      </main>
    );
  }

  if (session === undefined) {
    return (
      <main className="grid min-h-screen place-items-center text-sm text-muted-foreground">
        セッションを確認しています…
      </main>
    );
  }

  if (!session) {
    return (
      <>
        {authError ? (
          <Alert
            variant="destructive"
            className="fixed top-4 right-4 left-4 z-10 mx-auto max-w-lg"
          >
            <AlertDescription>{authError}</AlertDescription>
          </Alert>
        ) : null}
        <LoginForm client={supabase} />
      </>
    );
  }

  return (
    <div className="min-h-screen">
      <header className="border-b bg-card/80 backdrop-blur">
        <div className="mx-auto flex max-w-[112rem] items-center justify-between gap-4 px-4 py-3 sm:px-6 lg:px-8">
          <div>
            <p className="font-semibold">milledon 管理</p>
            <p className="text-xs text-muted-foreground">
              {session.user.email ?? session.user.id}
            </p>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => void handleSignOut()}
            disabled={isSigningOut}
          >
            {isSigningOut ? "ログアウト中…" : "ログアウト"}
          </Button>
        </div>
      </header>
      <nav className="border-b bg-card/40" aria-label="管理画面の表示切替">
        <div
          className="mx-auto flex max-w-[112rem] gap-6 overflow-x-auto px-4 sm:px-6 lg:px-8"
          role="tablist"
        >
          {(
            [
              ["status", "状況ボード"],
              ["cycles", "定期便の回"],
              ["inventory", "在庫・枠"],
              ["shipping", "出荷"],
              ["disaster", "災害停止"],
              ["logs", "操作ログ"],
              ["lookup", "決済照会"],
              ["admins", "管理者"],
            ] as const
          ).map(([value, label]) => (
            <button
              key={value}
              id={`${value}-tab`}
              type="button"
              role="tab"
              aria-selected={activeTab === value}
              aria-controls={`${value}-panel`}
              className={
                activeTab === value
                  ? "shrink-0 border-b-2 border-primary px-1 py-3 text-sm font-medium whitespace-nowrap text-foreground outline-none focus-visible:ring-3 focus-visible:ring-ring/50"
                  : "shrink-0 border-b-2 border-transparent px-1 py-3 text-sm font-medium whitespace-nowrap text-muted-foreground outline-none transition-colors hover:text-foreground focus-visible:ring-3 focus-visible:ring-ring/50"
              }
              onClick={() => setActiveTab(value)}
            >
              {label}
            </button>
          ))}
        </div>
      </nav>
      <main className="mx-auto max-w-[112rem] px-4 py-8 sm:px-6 lg:px-8">
        {authError ? (
          <Alert variant="destructive" className="mb-6">
            <AlertDescription>{authError}</AlertDescription>
          </Alert>
        ) : null}
        <div
          id="status-panel"
          role="tabpanel"
          aria-labelledby="status-tab"
          hidden={activeTab !== "status"}
        >
          {activeTab === "status" ? (
            <StatusBoard
              accessToken={session.access_token}
              userId={session.user.id}
            />
          ) : null}
        </div>
        <div
          id="cycles-panel"
          role="tabpanel"
          aria-labelledby="cycles-tab"
          hidden={activeTab !== "cycles"}
        >
          {activeTab === "cycles" ? (
            <SubscriptionCycles accessToken={session.access_token} />
          ) : null}
        </div>
        <div
          id="inventory-panel"
          role="tabpanel"
          aria-labelledby="inventory-tab"
          hidden={activeTab !== "inventory"}
        >
          {activeTab === "inventory" ? (
            <Inventory accessToken={session.access_token} />
          ) : null}
        </div>
        <div
          id="shipping-panel"
          role="tabpanel"
          aria-labelledby="shipping-tab"
          hidden={activeTab !== "shipping"}
        >
          {activeTab === "shipping" ? (
            <Shipping accessToken={session.access_token} />
          ) : null}
        </div>
        <div
          id="disaster-panel"
          role="tabpanel"
          aria-labelledby="disaster-tab"
          hidden={activeTab !== "disaster"}
        >
          {activeTab === "disaster" ? (
            <Disaster accessToken={session.access_token} />
          ) : null}
        </div>
        <div
          id="logs-panel"
          role="tabpanel"
          aria-labelledby="logs-tab"
          hidden={activeTab !== "logs"}
        >
          {activeTab === "logs" ? (
            <OperationLogs accessToken={session.access_token} />
          ) : null}
        </div>
        <div
          id="lookup-panel"
          role="tabpanel"
          aria-labelledby="lookup-tab"
          hidden={activeTab !== "lookup"}
        >
          {activeTab === "lookup" ? (
            <PaymentLookupPanel accessToken={session.access_token} />
          ) : null}
        </div>
        <div
          id="admins-panel"
          role="tabpanel"
          aria-labelledby="admins-tab"
          hidden={activeTab !== "admins"}
        >
          {activeTab === "admins" ? (
            <AdminUsers
              accessToken={session.access_token}
              currentUserId={session.user.id}
            />
          ) : null}
        </div>
      </main>
    </div>
  );
}
